//@flow

import {useEffect, useState} from 'react';
import {useUser} from '../../auth/utils';

import type {RealTimeEventData} from '../../../entities';

type UseEventSourceOptions = {
  onMessage: (data: RealTimeEventData) => void,
  onError: (e: Event) => void,
  onOpen?: (e: any) => void,
};

type EventSourceState = 'connecting' | 'open' | 'closed' | 'error';

export function useRealTimeListener(
  url?: string,
  options: UseEventSourceOptions
) {
  const user = useUser();

  const [sourceState, setSourceState] = useState<EventSourceState>('closed');
  const {onError, onMessage, onOpen} = options;

  const [eventSource, setEventSource] = useState<EventSource | null>(null);

  useEffect(() => {
    if (!user || !url) return;
    setSourceState('connecting');
    const eventSource = new EventSource(url);
    setEventSource(eventSource);

    const handleOpen = (event: any) => {
      setSourceState('open');
    };
    eventSource.addEventListener('open', handleOpen);

    return () => {
      eventSource.removeEventListener('open', handleOpen);
      eventSource.close();
      setSourceState('closed');
    };
  }, [url, user]);

  useEffect(() => {
    if (!eventSource) return;
    const handleError = (event: Event) => {
      setSourceState('error');
      if (onError) onError(event);
    };

    const handleOpen = (event: any) => {
      setSourceState('open');
      if (onOpen) onOpen(event);
    };

    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      const data: RealTimeEventData = JSON.parse(event.data);

      if (data.Type === 'keep-alive') return;

      onMessage(data);
    };

    eventSource.addEventListener('message', handleMessage);

    eventSource.addEventListener('error', handleError);

    eventSource.addEventListener('open', handleOpen);

    return () => {
      eventSource.removeEventListener('message', handleMessage);
      eventSource.removeEventListener('error', handleError);
      eventSource.removeEventListener('open', handleOpen);
    };
  }, [eventSource, onError, onMessage, onOpen]);

  return {
    connecting: sourceState === 'connecting',
    open: sourceState === 'open',
    closed: sourceState === 'closed',
  };
}
